import React, { useState } from 'react';
import { MaterialItem } from '@/types';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Edit, Trash2 } from 'lucide-react';
import { decimalToFraction } from '@/utils/measurementUtils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

interface MaterialListProps {
  items: MaterialItem[];
  onEditItem: (item: MaterialItem) => void;
  onDeleteItem: (itemId: string) => void;
}

const MaterialList: React.FC<MaterialListProps> = ({
  items,
  onEditItem,
  onDeleteItem,
}) => {
  const [editingItem, setEditingItem] = useState<MaterialItem | null>(null);
  const [editName, setEditName] = useState('');
  const [editLength, setEditLength] = useState('');
  const [editQuantity, setEditQuantity] = useState('');
  const [editNotes, setEditNotes] = useState('');

  const handleEdit = (item: MaterialItem) => {
    setEditingItem(item);
    setEditName(item.name);
    setEditLength(item.length.toString());
    setEditQuantity(item.quantity.toString());
    setEditNotes(item.notes || '');
  };

  const handleSaveEdit = () => {
    if (!editingItem) return;

    const updatedItem: MaterialItem = {
      ...editingItem,
      name: editName.trim() || editingItem.name,
      length: parseFloat(editLength) || editingItem.length,
      quantity: parseInt(editQuantity) || editingItem.quantity,
      notes: editNotes.trim(),
    };

    onEditItem(updatedItem);
    setEditingItem(null);
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
  };

  // Group items by category
  const itemsByCategory = items.reduce((acc, item) => {
    if (!acc[item.categoryId]) {
      acc[item.categoryId] = [];
    }
    acc[item.categoryId].push(item);
    return acc;
  }, {} as Record<string, MaterialItem[]>);

  if (items.length === 0) {
    return (
      <div className="text-center p-8 border border-dashed rounded-lg">
        <p className="text-muted-foreground">No materials added yet. Select a material type above to get started.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {Object.entries(itemsByCategory).map(([categoryId, categoryItems]) => (
        <div key={categoryId} className="space-y-2">
          <h3 className="text-lg font-medium">
            {categoryItems[0]?.name.split(' ')[0]} {/* Use first word of item name as category name */}
          </h3>

          <div className="grid grid-cols-1 gap-2">
            {categoryItems.map((item) => (
              <Card key={item.id} className="overflow-hidden">
                <CardContent className="p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <h4 className="font-medium">{item.name}</h4>
                      <div className="text-sm text-muted-foreground">
                        <span>Length: {decimalToFraction(item.length)} inches</span>
                        <span className="mx-2">•</span>
                        <span>Quantity: {item.quantity}</span>
                      </div>
                      {item.notes && (
                        <p className="text-sm mt-1 text-muted-foreground">{item.notes}</p>
                      )}
                    </div>

                    <div className="flex space-x-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(item)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onDeleteItem(item.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ))}

      {/* Edit Dialog */}
      <Dialog open={!!editingItem} onOpenChange={(open) => !open && handleCancelEdit()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Material</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="edit-name">Name</Label>
                <Input
                  id="edit-name"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="edit-length">Length (inches)</Label>
                <Input
                  id="edit-length"
                  type="number"
                  step="0.125"
                  min="0.125"
                  value={editLength}
                  onChange={(e) => setEditLength(e.target.value)}
                  required
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="edit-quantity">Quantity</Label>
                <Input
                  id="edit-quantity"
                  type="number"
                  min="1"
                  value={editQuantity}
                  onChange={(e) => setEditQuantity(e.target.value)}
                  required
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="edit-notes">Notes (optional)</Label>
                <Textarea
                  id="edit-notes"
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleCancelEdit}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSaveEdit}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MaterialList;
